import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { ScrollArea } from './components/ui/scroll-area';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';
import { FileText, Plus, Clock, CheckCircle, XCircle, Search, Filter, Calendar, Zap, TrendingUp, Download, FileDown, Printer, Copy, Mic, MicOff, Trash2, AlertTriangle, LogOut, User, Users, CreditCard, Shield } from 'lucide-react';
import axios from 'axios';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== Session Management (dual: cookie + localStorage) =====
const SESSION_KEY = 'adas_session_token';
const getSessionToken = () => localStorage.getItem(SESSION_KEY);
const saveSessionToken = (token) => { if (token) localStorage.setItem(SESSION_KEY, token); };
const clearSessionToken = () => localStorage.removeItem(SESSION_KEY);

// Axios interceptor: attach auth token to every request
axios.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EXAMINATION_TYPES = [
  { value: 'x-ray', label: 'X-Ray' },
  { value: 'ct-scan', label: 'CT Scan' },
  { value: 'mri', label: 'MRI' },
  { value: 'ultrasound', label: 'Ultrasound' },
  { value: '2d-echo', label: '2D Echo' },
  { value: 'mammography', label: 'Mammography' },
  { value: 'ivp', label: 'IVP (Intravenous Pyelography)' },
  { value: 'barium-study', label: 'Barium Study' },
  { value: 'mcu', label: 'MCU (Micturating Cystourethrography)' }
];

// Auth Callback Component - handles OAuth redirect
function AuthCallback({ onAuthSuccess }) {
  const hasProcessed = useRef(false);
  const { toast } = useToast();
  const [authError, setAuthError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const doAuth = async (retryCount = 0) => {
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (!sessionIdMatch) {
      setAuthError('No session found in URL. Please try signing in again.');
      return;
    }

    const sessionId = sessionIdMatch[1];
    
    try {
      const response = await axios.post(`${API}/auth/session`, {
        session_id: sessionId
      }, { timeout: 15000 });
      
      if (response.data.success) {
        saveSessionToken(response.data.session_token);
        window.history.replaceState(null, '', window.location.pathname);
        onAuthSuccess(response.data.user);
        toast({
          title: "Welcome!",
          description: `Signed in as ${response.data.user.name}`,
        });
      } else {
        throw new Error('Session exchange returned unsuccessful response');
      }
    } catch (error) {
      console.error(`Auth error (attempt ${retryCount + 1}):`, error?.response?.status, error?.message);
      
      // Retry up to 2 times with delay
      if (retryCount < 2) {
        setRetrying(true);
        await new Promise(r => setTimeout(r, 1500));
        return doAuth(retryCount + 1);
      }
      
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      setAuthError(`Sign-in failed: ${detail}`);
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    doAuth();
  }, []);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-xl">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-800 mb-2">Sign-in Issue</h2>
          <p className="text-sm text-gray-600 mb-4">{authError}</p>
          <div className="space-y-2">
            <Button 
              onClick={() => {
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full bg-[#0d5c63] hover:bg-[#0a4a50] text-white"
            >
              Try Sign In Again
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = window.location.origin + '/'}
              className="w-full"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="text-center">
        <Clock className="h-12 w-12 animate-spin text-[#0f7178] mx-auto mb-4" />
        <p className="text-white text-lg">{retrying ? 'Retrying sign in...' : 'Signing you in...'}</p>
      </div>
    </div>
  );
}

// Login Page Component
function LoginPage() {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: Zap, title: 'Reports in < 60s', text: 'Structured TECHNIQUE → FINDINGS → IMPRESSION', color: '#2563eb' },
    { icon: Shield, title: 'MRI · CT · X-Ray · USG', text: 'Images, DICOM, ZIP & video', color: '#0f766e' },
    { icon: Mic, title: 'Voice Dictation', text: 'English + Hindi radiology shortforms', color: '#e11d48' },
    { icon: FileDown, title: 'PDF & Word', text: 'One-click export for referral', color: '#d97706' },
  ];

  return (
    <div className="login-container" data-testid="landing-page">
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-16 min-h-screen flex flex-col">
        <div className="flex items-center justify-between fade-up">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="GB ADAS" className="h-12 w-12 object-contain" />
            <span className="font-display text-2xl font-black tracking-tight text-slate-900">ADAS-E</span>
          </div>
          <span className="conf-banner" data-testid="conf-banner"><span className="live-dot"></span>State Radiology Conference · Live Demo</span>
        </div>

        <div className="flex-1 grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16 items-center mt-12 lg:mt-0">
          <div>
            <p className="font-mono-clin text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-blue-700 fade-up" style={{ animationDelay: '0.05s' }}>Advanced Diagnostic Assistance System</p>
            <h1 className="hero-title text-6xl sm:text-7xl lg:text-8xl mt-4 fade-up" style={{ animationDelay: '0.12s' }}>ADAS-E</h1>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-5 leading-[1.05] fade-up" style={{ animationDelay: '0.2s' }}>
              Structured radiology reports.<br /><span className="text-blue-600">In seconds.</span>
            </p>
            <p className="text-base sm:text-lg text-slate-600 font-medium mt-5 italic fade-up" style={{ animationDelay: '0.28s' }}>"Here to Assist — Not to Replace"</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8">
              {features.map((f, i) => (
                <div key={f.title} className="feature-tile fade-up" style={{ animationDelay: `${0.35 + i * 0.08}s` }} data-testid={`feature-tile-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${f.color}14`, color: f.color }}><f.icon className="h-5 w-5" /></span>
                    <div>
                      <p className="font-display font-extrabold text-slate-900 leading-tight">{f.title}</p>
                      <p className="text-xs text-slate-500 font-medium mt-0.5">{f.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="login-card p-8 sm:p-10 fade-up" style={{ animationDelay: '0.3s' }} data-testid="login-card">
            <p className="font-mono-clin text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Start free</p>
            <p className="font-display text-6xl font-black text-slate-900 mt-3 leading-none" data-testid="free-scans-highlight">5 <span className="text-blue-600">FREE</span></p>
            <p className="font-display text-2xl font-extrabold text-slate-900 mt-1">scans on sign-up</p>
            <p className="text-sm text-slate-500 font-medium mt-3">No card needed. Then just <strong className="text-slate-900">₹100 for 10 scans</strong>.</p>
            <Button
              onClick={handleGoogleLogin}
              data-testid="google-signin-btn"
              className="w-full h-14 mt-8 text-base font-bold bg-white hover:bg-white text-slate-800 border-2 border-slate-200 hover:border-blue-500 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
            <p className="text-xs text-slate-400 text-center mt-5">By signing in, you agree to our Terms of Service</p>
            <p className="font-mono-clin text-[11px] font-bold uppercase tracking-[0.18em] text-center text-blue-700 mt-6 pt-5 border-t border-slate-100">Powered by AI · Made for Radiologists</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pricing Modal — In-app Razorpay Checkout (server creates order, captures user email automatically)
function PricingModal({ isOpen, onClose, user, onPaymentSuccess }) {
  const { toast } = useToast();
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleBuy = async () => {
    if (processing) return;
    setProcessing(true);

    // Check Razorpay SDK is loaded
    if (typeof window.Razorpay === "undefined") {
      toast({ title: "Error", description: "Payment SDK not loaded. Please refresh the page.", variant: "destructive" });
      setProcessing(false);
      return;
    }

    try {
      // 1) Create order on backend (server-side: uses live API keys, persists order)
      const { data: order } = await axios.post(`${API}/payments/create-order`, { plan_id: "starter" });
      if (!order?.order_id) throw new Error("Order creation failed");

      // 2) Open Razorpay Checkout
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "ADAS-E",
        description: order.description,
        order_id: order.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        notes: { user_email: user?.email },
        theme: { color: "#0d5c63" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast({ title: "Payment Successful", description: "10 scans have been added to your account!" });
            onPaymentSuccess?.();
            onClose();
          } catch (e) {
            toast({ title: "Verification pending", description: "Payment received. Credits will be added shortly via webhook.", variant: "destructive" });
            onClose();
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast({ title: "Payment Failed", description: resp?.error?.description || "Please try again", variant: "destructive" });
        setProcessing(false);
      });
      rzp.open();
      // Reset processing after Razorpay opens (it's modal — user can close it)
      setTimeout(() => setProcessing(false), 1500);
    } catch (err) {
      toast({
        title: "Could not start payment",
        description: err?.response?.data?.detail || err.message || "Please try again.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md overflow-hidden shadow-2xl border-0" data-testid="pricing-modal">
        <CardHeader style={{ background: 'linear-gradient(135deg, #0d5c63 0%, #094147 100%)', borderRadius: '12px 12px 0 0' }}>
          <CardTitle className="text-xl text-center text-white font-extrabold tracking-wide">Buy Scans</CardTitle>
          <CardDescription className="text-center text-gray-200">
            {user?.free_scans_remaining > 0
              ? `You have ${user.free_scans_remaining} free scan(s) remaining`
              : "Top up to keep generating reports"}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 pb-5 space-y-4">
          <div className="rounded-xl p-5 border-2" style={{ borderColor: '#14919b', background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)' }}>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-lg font-extrabold text-teal-800 uppercase tracking-wider">Starter Pack</span>
              <span className="text-3xl font-extrabold text-teal-700">₹100</span>
            </div>
            <div className="text-sm text-slate-600 mb-4">
              <span className="font-bold text-teal-700">10 scans</span> · just <span className="font-semibold">₹10 per report</span>
            </div>
            <button
              type="button"
              onClick={handleBuy}
              disabled={processing}
              data-testid="buy-starter-pack-btn"
              className="block w-full text-center py-3 rounded-lg font-extrabold text-white text-sm tracking-wider uppercase shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #0d5c63, #14919b)' }}
            >
              {processing ? "Opening payment..." : "Pay ₹100 · Get 10 Scans"}
            </button>
            <p className="text-[11px] text-center text-slate-500 mt-3">
              UPI · Cards · Net Banking · Secure via Razorpay
            </p>
          </div>

          <div className="text-center">
            <Button variant="outline" onClick={onClose} data-testid="pricing-close-btn" className="border-teal-200 text-teal-700 hover:bg-teal-50">
              Maybe Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// User Stats Dashboard Component
function UserStatsDashboard({ user, stats, onClose }) {
  if (!stats) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            My Account
          </CardTitle>
          <CardDescription>{user?.email}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Credits Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#f0f9f9] p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-[#0d5c63]">
                {stats.total_scans_available === -1 ? '∞' : stats.total_scans_available}
              </p>
              <p className="text-sm text-gray-600">Scans Available</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-600">{stats.total_reports_created}</p>
              <p className="text-sm text-gray-600">Reports Created</p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Free Scans Used:</span>
              <span className="font-medium">{stats.free_scans_used} / 3</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Free Scans Remaining:</span>
              <span className="font-medium text-green-600">{stats.free_scans_remaining}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Purchased Scans:</span>
              <span className="font-medium">{stats.purchased_scans_remaining}</span>
            </div>
            {stats.subscription_active && (
              <div className="flex justify-between">
                <span className="text-gray-600">Subscription:</span>
                <span className="font-medium text-purple-600">
                  {stats.subscription_days_remaining} days remaining
                </span>
              </div>
            )}
          </div>

          <Separator />

          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Admin Panel Component
function AdminPanel({ onClose }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addCreditsEmail, setAddCreditsEmail] = useState('');
  const [addCreditsScans, setAddCreditsScans] = useState(0);
  const [addCreditsDays, setAddCreditsDays] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchUser, setSearchUser] = useState('');

  const fetchAdminData = useCallback(async () => {
    try {
      const [usersRes, paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/stats`),
      ]);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.payments);
      setAdminStats(statsRes.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        toast({
          title: "Session Expired",
          description: "Please sign in again to use Admin Panel.",
          variant: "destructive",
        });
        clearSessionToken();
        onClose();
        setTimeout(() => window.location.reload(), 800);
      } else if (status === 403) {
        toast({
          title: "Access Denied",
          description: "You don't have admin access.",
          variant: "destructive",
        });
        onClose();
      } else {
        toast({
          title: "Error loading admin data",
          description: error?.response?.data?.detail || error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast, onClose]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleAddCredits = async () => {
    if (!addCreditsEmail) {
      toast({ title: "Error", description: "Enter user email", variant: "destructive" });
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/add-credits`, {
        email: addCreditsEmail,
        scans: addCreditsScans,
        days: addCreditsDays
      });

      toast({
        title: "Credits Added",
        description: response.data.message,
      });

      setAddCreditsEmail('');
      setAddCreditsScans(0);
      setAddCreditsDays(0);
      fetchAdminData();
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        toast({
          title: "Session Expired",
          description: "Please re-login as admin to add credits.",
          variant: "destructive",
        });
        clearSessionToken();
        setTimeout(() => window.location.reload(), 1200);
        return;
      }
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add credits",
        variant: "destructive",
      });
    }
  };

  const filteredUsers = users.filter(u => 
    !searchUser || u.email.toLowerCase().includes(searchUser.toLowerCase()) || (u.name || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <Card className="p-8 bg-slate-900 border-slate-700">
          <Clock className="h-8 w-8 animate-spin mx-auto text-teal-400" />
          <p className="mt-3 text-slate-300">Loading admin panel...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-5xl max-h-[92vh] flex flex-col bg-slate-900 rounded-xl border border-slate-700 shadow-2xl overflow-hidden" data-testid="admin-panel">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-teal-600/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-teal-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white tracking-tight">Admin Dashboard</h2>
              <p className="text-xs text-slate-400">ADAS-E Management Console</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white hover:bg-slate-700" data-testid="admin-close-btn">
            <XCircle className="h-5 w-5" />
          </Button>
        </div>

        {/* Stats Cards */}
        {adminStats && (
          <div className="grid grid-cols-4 gap-3 px-6 py-4 bg-slate-800/50">
            <div className="rounded-lg p-3 bg-gradient-to-br from-teal-900/40 to-teal-800/20 border border-teal-700/30" data-testid="stat-total-users">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-teal-400" />
                <span className="text-xs font-medium text-teal-300 uppercase tracking-wide">Users</span>
              </div>
              <p className="text-2xl font-bold text-white">{adminStats.total_users}</p>
            </div>
            <div className="rounded-lg p-3 bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 border border-emerald-700/30" data-testid="stat-total-reports">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-4 w-4 text-emerald-400" />
                <span className="text-xs font-medium text-emerald-300 uppercase tracking-wide">Reports</span>
              </div>
              <p className="text-2xl font-bold text-white">{adminStats.total_reports}</p>
            </div>
            <div className="rounded-lg p-3 bg-gradient-to-br from-violet-900/40 to-violet-800/20 border border-violet-700/30" data-testid="stat-total-payments">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="h-4 w-4 text-violet-400" />
                <span className="text-xs font-medium text-violet-300 uppercase tracking-wide">Payments</span>
              </div>
              <p className="text-2xl font-bold text-white">{adminStats.total_payments}</p>
            </div>
            <div className="rounded-lg p-3 bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-700/30" data-testid="stat-total-revenue">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-amber-400" />
                <span className="text-xs font-medium text-amber-300 uppercase tracking-wide">Revenue</span>
              </div>
              <p className="text-2xl font-bold text-white">&#8377;{adminStats.total_revenue}</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 px-6 pt-3 pb-0 border-b border-slate-700">
          {[
            { id: 'overview', label: 'Add Credits', icon: Plus },
            { id: 'users', label: `Users (${users.length})`, icon: Users },
            { id: 'payments', label: `Payments (${payments.length})`, icon: CreditCard },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-slate-800 text-teal-400 border-t-2 border-teal-400' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto p-6">
          
          {/* Add Credits Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="rounded-lg p-5 bg-slate-800 border border-slate-700">
                <h3 className="text-sm font-semibold text-teal-400 uppercase tracking-wide mb-4">Add Credits to User</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-1">
                    <label className="text-xs text-slate-400 mb-1 block">User Email</label>
                    <Input
                      placeholder="user@email.com"
                      value={addCreditsEmail}
                      onChange={(e) => setAddCreditsEmail(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="admin-credits-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Scans to Add</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={addCreditsScans}
                      onChange={(e) => setAddCreditsScans(parseInt(e.target.value) || 0)}
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="admin-credits-scans"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Days to Add</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={addCreditsDays}
                      onChange={(e) => setAddCreditsDays(parseInt(e.target.value) || 0)}
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="admin-credits-days"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddCredits} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="admin-add-credits-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add Credits
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick User Lookup */}
              {adminStats?.recent_users && (
                <div className="rounded-lg p-5 bg-slate-800 border border-slate-700">
                  <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wide mb-3">Recent Signups</h3>
                  <div className="space-y-2">
                    {adminStats.recent_users.map((u, i) => (
                      <div key={u.user_id || u.email || i} className="flex items-center justify-between py-2 px-3 rounded-md bg-slate-900/50 border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-teal-600/20 flex items-center justify-center text-teal-400 text-sm font-bold">
                            {(u.name || u.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm text-white font-medium">{u.name || 'Unknown'}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="admin-search-users"
                  />
                </div>
                <span className="text-xs text-slate-400">{filteredUsers.length} users</span>
              </div>
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <div className="max-h-[50vh] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800 sticky top-0">
                      <tr>
                        <th className="p-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wide">User</th>
                        <th className="p-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wide">Free</th>
                        <th className="p-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wide">Purchased</th>
                        <th className="p-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wide">Reports</th>
                        <th className="p-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wide">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.user_id} className="border-t border-slate-700/50 hover:bg-slate-800/60 transition-colors">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="h-7 w-7 rounded-full bg-teal-600/20 flex items-center justify-center text-teal-400 text-xs font-bold shrink-0">
                                {(u.name || u.email || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">{u.name || '—'}</p>
                                <p className="text-xs text-slate-400">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`text-sm font-medium ${u.free_scans_remaining > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                              {u.free_scans_remaining}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`text-sm font-medium ${u.purchased_scans > 0 ? 'text-teal-400' : 'text-slate-500'}`}>
                              {u.purchased_scans}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-sm text-slate-300">{u.total_reports || 0}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">{payments.length} transactions</span>
                {adminStats && (
                  <span className="text-sm font-semibold text-amber-400">Total: &#8377;{adminStats.total_revenue}</span>
                )}
              </div>
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <div className="max-h-[50vh] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800 sticky top-0">
                      <tr>
                        <th className="p-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wide">User</th>
                        <th className="p-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wide">Plan</th>
                        <th className="p-3 text-center text-xs font-semibold text-slate-300 uppercase tracking-wide">Amount</th>
                        <th className="p-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wide">Source</th>
                        <th className="p-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wide">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p, i) => (
                        <tr key={p.razorpay_payment_id || p.payment_id || i} className="border-t border-slate-700/50 hover:bg-slate-800/60 transition-colors">
                          <td className="p-3 text-white text-sm">{p.user_email}</td>
                          <td className="p-3">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-teal-900/40 text-teal-300 border border-teal-700/30">
                              {p.plan_id}
                            </span>
                          </td>
                          <td className="p-3 text-center text-amber-400 font-semibold">&#8377;{p.amount}</td>
                          <td className="p-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                              p.source === 'webhook' ? 'bg-emerald-900/40 text-emerald-300' : 'bg-blue-900/40 text-blue-300'
                            }`}>
                              {p.source || 'direct'}
                            </span>
                          </td>
                          <td className="p-3 text-slate-400 text-sm">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                      {payments.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-500">No payments yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { toast } = useToast();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('create');
  const [showPricing, setShowPricing] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    examination_type: '',
    body_part_region: '',
    key_findings: '',
    clinical_history: '',
    diagnostic_clues: '',
    patient_name: '',
    patient_age: '',
    patient_sex: '',
    referred_by: ''
  });

  // Voice recognition state
  const [isRecording, setIsRecording] = useState(false);
  const [activeVoiceField, setActiveVoiceField] = useState('key_findings');
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [selectedReportsForDelete, setSelectedReportsForDelete] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isLoadingReportDetails, setIsLoadingReportDetails] = useState(false);
  
  // Whisper transcription state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);

  // Image upload state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isAnalyzingImages, setIsAnalyzingImages] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [pacsUrl, setPacsUrl] = useState('');

  // Check for OAuth callback on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setAuthLoading(false);
      return;
    }
    
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        
        // Check if admin
        const adminEmails = ['radiojalna@gmail.com', 'adas@germanbabdi.com'];
        setIsAdmin(adminEmails.includes(response.data.email));
        
        // Fetch user stats
        try {
          const statsResponse = await axios.get(`${API}/auth/stats`);
          setUserStats(statsResponse.data.stats);
        } catch (e) {
          console.log('Failed to fetch stats');
        }
        
        fetchReports();
      } catch (error) {
        console.log('Not authenticated:', error?.response?.status);
        // Only clear session if genuinely unauthorized (401) or no stored token
        if (error?.response?.status === 401) {
          clearSessionToken();
        }
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: "Error",
        description: "Failed to fetch reports",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!user) return;
    fetchReports();
  }, [user, fetchReports]);

  // Logout function
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      clearSessionToken();
      setUser(null);
      setReports([]);
      toast({
        title: "Logged Out",
        description: "You have been signed out successfully.",
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if server call fails, clear local state
      clearSessionToken();
      setUser(null);
      setReports([]);
    }
  };

  // Check if user can create scan (has credits)
  const checkAndUseScan = async () => {
    if (!user) return false;
    
    // Check if user can scan
    if (!user.can_scan) {
      setShowPricing(true);
      toast({
        title: "No Scans Remaining",
        description: "Please purchase more scans to continue.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      const response = await axios.post(`${API}/auth/use-scan`, {});
      if (response.data.success) {
        // Only update user state if user data is returned
        if (response.data.user) {
          setUser(response.data.user);
        }
        return true;
      } else {
        setShowPricing(true);
        return false;
      }
    } catch (error) {
      console.error('Failed to use scan:', error);
      if (error?.response?.status === 401) {
        // Session expired - don't show pricing, show a clear message
        toast({
          title: "Session Expired",
          description: "Please refresh the page to continue.",
          variant: "destructive",
        });
      } else {
        setShowPricing(true);
      }
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.examination_type || !formData.key_findings) {
      toast({
        title: "Validation Error",
        description: "Please select examination type and enter key findings",
        variant: "destructive",
      });
      return;
    }

    // Save session token before long-running request (prevent session loss)
    const currentToken = getSessionToken();

    // Check and deduct scan before creating report
    const canProceed = await checkAndUseScan();
    if (!canProceed) return;

    try {
      setLoading(true);
      const reportData = {
        ...formData,
        pacs_url: pacsUrl || null
      };
      
      // Set a long timeout for report generation (it can take 90+ seconds)
      const response = await axios.post(`${API}/reports`, reportData, { 
        timeout: 180000 // 3 minutes
      });
      
      toast({
        title: "Report Generated Successfully!",
        description: "Your report is ready. Switching to View Reports...",
      });
      
      // Reset form
      setFormData({
        examination_type: '',
        body_part_region: '',
        key_findings: '',
        clinical_history: '',
        diagnostic_clues: '',
        patient_name: '',
        patient_age: '',
        patient_sex: '',
        referred_by: ''
      });
      setPacsUrl('');
      setUploadedImages([]);
      setImagePreviews([]);
      
      // Refresh reports list
      await fetchReports();
      
      // Auto-switch to reports tab to show the new report
      setActiveTab('reports');
      
      // Refresh user scan count (don't let this fail affect anything)
      try {
        const userResponse = await axios.get(`${API}/auth/me`);
        if (userResponse.data) setUser(userResponse.data);
      } catch (e) {
        // Restore token if auth refresh fails
        if (currentToken) saveSessionToken(currentToken);
      }
      
    } catch (error) {
      console.error('Error creating report:', error);
      
      // Restore session token if it was lost during the error
      if (currentToken) saveSessionToken(currentToken);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast({
          title: "Report is being generated...",
          description: "Report generation is taking longer than usual. Please check View Reports in a minute.",
          variant: "default",
        });
        // Still try to refresh reports after a delay
        setTimeout(async () => {
          await fetchReports();
          setActiveTab('reports');
        }, 5000);
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to create report. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch full report details when selecting a report
  const handleSelectReport = async (report) => {
    // Set immediately for quick UI response
    setSelectedReport(report);
    setIsLoadingReportDetails(true);
    
    // Fetch full report details (including detailed_report which is excluded from list query)
    try {
      const response = await axios.get(`${API}/reports/${report.id}`);
      setSelectedReport(response.data);
      console.log('Fetched full report:', response.data.id, 'Has detailed_report:', !!response.data.detailed_report);
    } catch (error) {
      console.error('Error fetching report details:', error);
      toast({
        title: "Warning",
        description: "Could not load full report details. Some information may be missing.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingReportDetails(false);
    }
  };


  // Image handling functions
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // ADDITIVE: Append new files to existing ones (don't replace)
    setUploadedImages(prev => [...prev, ...files]);

    // Create previews for new files, append to existing
    const newPreviews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({
          name: file.name,
          url: e.target.result,
          type: file.type
        });
        if (newPreviews.length === files.length) {
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  const handleAnalyzeImages = async () => {
    // Check if either files or URL provided
    if (uploadedImages.length === 0 && !pacsUrl) {
      toast({
        title: "No Input",
        description: "Please upload files or provide a download URL",
        variant: "destructive"
      });
      return;
    }

    if (!formData.examination_type) {
      toast({
        title: "Examination Type Required",
        description: "Please select an examination type before analyzing images",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzingImages(true);

    // Show processing toast for large batches
    const imageCount = uploadedImages.length || 1;
    const isZipFile = uploadedImages.some(f => f.name.toLowerCase().endsWith('.zip')) || pacsUrl?.toLowerCase().endsWith('.zip');
    const estimatedTime = isZipFile ? '3-8 minutes' : imageCount > 10 ? '3-6 minutes' : imageCount > 5 ? '1-3 minutes' : '30-60 seconds';
    
    toast({
      title: "🔄 Uploading Images...",
      description: `Processing ${imageCount} file(s). ${isZipFile ? 'ZIP files are processed in background.' : `Estimated time: ${estimatedTime}.`}`,
      duration: 10000,
    });

    try {
      const formDataToSend = new FormData();
      
      // Add uploaded files if any
      uploadedImages.forEach(file => {
        formDataToSend.append('files', file);
      });
      
      formDataToSend.append('examination_type', formData.examination_type);
      formDataToSend.append('clinical_history', formData.clinical_history || '');
      formDataToSend.append('diagnostic_clues', formData.diagnostic_clues || '');
      
      // Add download URL if provided
      if (pacsUrl) {
        formDataToSend.append('download_url', pacsUrl);
      }

      const response = await axios.post(`${API}/analyze-images`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minutes timeout - background tasks return immediately
        onUploadProgress: (progressEvent) => {
          console.log('Upload progress:', Math.round((progressEvent.loaded * 100) / progressEvent.total), '%');
        }
      });

      console.log('🔍 Analysis response received:', response.data);

      if (response.data.success) {
        // Check if this is an async task
        if (response.data.async && response.data.task_id) {
          console.log('📋 Async task started:', response.data.task_id);
          
          toast({
            title: "🔄 Processing Large Batch...",
            description: `Task ${response.data.task_id.substring(0, 8)}... started. Estimated time: ${response.data.estimated_time_minutes} minute(s). Polling for results...`,
            duration: 15000,
          });
          
          // Start polling for results
          await pollAnalysisTask(response.data.task_id);
          
        } else {
          // Synchronous result - handle immediately
          const findings = response.data.key_findings;
          console.log('🎯 Setting key findings to field:', findings?.substring(0, 100) + '...');
          
          handleInputChange('key_findings', findings);
          
          toast({
            title: "✅ Images Analyzed Successfully",
            description: `Processed ${response.data.images_processed} image(s). Key findings extracted.`,
            duration: 5000,
          });
        }
      } else {
        console.error('❌ Response success was false');
        toast({
          title: "Analysis Incomplete",
          description: "Analysis completed but no findings returned. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error analyzing images:', error);
      
      let errorMessage = "Failed to analyze images. Please try again.";
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = "Upload timeout. For very large ZIP files, please try splitting into smaller files.";
      } else if (error.response?.status === 502) {
        errorMessage = "Server timeout. This usually happens with very large files. Try with fewer images.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Image Analysis Issue",
        description: errorMessage,
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setIsAnalyzingImages(false);
    }
  };

  // Poll for analysis task completion
  const pollAnalysisTask = async (taskId) => {
    const maxAttempts = 120; // 10 minutes max (5 second intervals)
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        
        const response = await axios.get(`${API}/analysis-tasks/${taskId}`);
        const task = response.data;
        
        console.log(`📊 Task ${taskId.substring(0, 8)}... status: ${task.status}, progress: ${task.progress}%`);
        
        if (task.status === 'completed') {
          console.log('✅ Task completed!');
          handleInputChange('key_findings', task.key_findings);
          
          toast({
            title: "✅ Analysis Complete!",
            description: `Processed ${task.images_count} images. Key findings extracted.`,
            duration: 5000,
          });
          return;
          
        } else if (task.status === 'failed') {
          console.error('❌ Task failed:', task.error_message);
          toast({
            title: "Analysis Failed",
            description: task.error_message || "Background analysis failed. Please try again.",
            variant: "destructive",
            duration: 8000,
          });
          return;
          
        } else {
          // Still processing - update UI
          if (attempts % 4 === 0) { // Every 20 seconds
            toast({
              title: `⏳ Processing... ${task.progress}%`,
              description: `Analyzing ${task.images_count} images. Please wait...`,
              duration: 5000,
            });
          }
        }
        
        attempts++;
        
      } catch (error) {
        console.error('Error polling task:', error);
        attempts++;
        
        if (attempts >= maxAttempts) {
          toast({
            title: "Polling Timeout",
            description: "Analysis is taking longer than expected. Please check back later.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };


  // Removed autocorrection function — now handled server-side by Whisper + AI post-processing

  const checkMicrophonePermission = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        console.log('Microphone permission granted');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      toast({
        title: "Microphone Permission Required",
        description: "Please allow microphone access in your browser settings and refresh the page.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Generic voice input for any field — Records audio, sends to Whisper backend
  const startVoiceForField = async (fieldName) => {
    console.log(`Starting voice recording for field: ${fieldName}`);
    
    // If already recording for this field, stop it
    if (isRecording && activeVoiceField === fieldName) {
      stopVoiceRecognition();
      return;
    }
    
    // If recording for another field, stop first
    if (isRecording) {
      stopVoiceRecognition();
    }
    
    // Check microphone permission
    const hasPermission = await checkMicrophonePermission();
    if (!hasPermission) return;

    const fieldLabels = {
      'body_part_region': 'Body Part/Region',
      'clinical_history': 'Clinical History',
      'key_findings': 'Key Findings',
      'diagnostic_clues': 'Diagnostic Clues'
    };

    // Set which field is active
    setActiveVoiceField(fieldName);
    setIsRecording(true);
    setCurrentTranscript('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      mediaStreamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Stop the media stream tracks
        stream.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];

        if (audioBlob.size < 1000) {
          toast({
            title: "Recording Too Short",
            description: "Please speak for a bit longer and try again.",
            variant: "destructive",
          });
          setIsRecording(false);
          setIsTranscribing(false);
          return;
        }

        // Now transcribe via Whisper
        setIsTranscribing(true);
        setCurrentTranscript('Processing with AI...');

        try {
          const formDataUpload = new FormData();
          formDataUpload.append('audio', audioBlob, 'recording.webm');

          const response = await axios.post(`${API}/transcribe`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          });

          const { transcript, corrections_applied } = response.data;

          if (transcript && transcript.trim()) {
            const currentValue = formData[fieldName] || '';
            const newValue = currentValue + (currentValue && !currentValue.endsWith(' ') ? ' ' : '') + transcript.trim();
            handleInputChange(fieldName, newValue);

            toast({
              title: `Transcribed: ${fieldLabels[fieldName] || fieldName}`,
              description: corrections_applied 
                ? "Medical terms auto-corrected by AI" 
                : "Transcription complete",
              duration: 3000,
            });
          } else {
            toast({
              title: "No Speech Detected",
              description: "Couldn't detect any speech. Please try again.",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Whisper transcription error:', error);
          toast({
            title: "Transcription Failed",
            description: error.response?.data?.detail || "Could not process audio. Try again.",
            variant: "destructive",
          });
        } finally {
          setIsTranscribing(false);
          setCurrentTranscript('');
        }
      };

      recorder.start(250); // collect chunks every 250ms
      mediaRecorderRef.current = recorder;

      toast({
        title: `Recording: ${fieldLabels[fieldName] || fieldName}`,
        description: "Speak now... Click mic again to stop & transcribe.",
        duration: 3000,
      });

    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);
      toast({
        title: "Cannot Start Recording",
        description: "Please check microphone permissions and try again.",
        variant: "destructive",
      });
    }
  };

  const startVoiceRecognition = async () => {
    await startVoiceForField('key_findings');
  };

  const stopVoiceRecognition = () => {
    // Stop MediaRecorder — this triggers the onstop handler which does the Whisper upload
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.log('Error stopping MediaRecorder:', e);
      }
      mediaRecorderRef.current = null;
    }
    
    // Stop media stream if recorder didn't
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    setIsRecording(false);
  };


  const handleDeleteReport = async (reportId, reportName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the report for "${reportName || 'Unknown Patient'}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      setLoading(true);
      
      await axios.delete(`${API}/reports/${reportId}`);
      
      toast({
        title: "Report Deleted",
        description: "Report has been permanently deleted.",
      });
      
      // Refresh reports list
      await fetchReports();
      
      // Clear selected report if it was the deleted one
      if (selectedReport && selectedReport.id === reportId) {
        setSelectedReport(null);
      }
      
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        title: "Delete Failed",
        description: error.response?.data?.detail || "Failed to delete report",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedReportsForDelete.length === 0) {
      toast({
        title: "No Reports Selected",
        description: "Please select reports to delete first.",
        variant: "destructive",
      });
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedReportsForDelete.length} selected reports?\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      setLoading(true);
      
      // Delete all selected reports
      const deletePromises = selectedReportsForDelete.map(reportId => 
        axios.delete(`${API}/reports/${reportId}`)
      );
      
      await Promise.all(deletePromises);
      
      toast({
        title: "Bulk Delete Complete",
        description: `Successfully deleted ${selectedReportsForDelete.length} reports.`,
      });
      
      // Clear selections and refresh
      setSelectedReportsForDelete([]);
      setSelectedReport(null);
      await fetchReports();
      
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast({
        title: "Bulk Delete Failed",
        description: "Some reports may not have been deleted. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleReportSelection = (reportId) => {
    setSelectedReportsForDelete(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const selectAllReports = () => {
    const allIds = filteredReports.map(report => report.id);
    setSelectedReportsForDelete(allIds);
  };

  const clearSelection = () => {
    setSelectedReportsForDelete([]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'enhanced':
        return <CheckCircle className="h-4 w-4 text-[#0d5c63]" />;
      case 'generated':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status, metadata = null) => {
    const variants = {
      enhanced: 'default',
      generated: 'secondary', 
      pending: 'secondary',
      error: 'destructive'
    };
    
    const getStatusText = (status) => {
      if (status === 'enhanced') return 'Enhanced AI';
      return status;
    };
    
    return (
      <div className="flex items-center gap-2">
        <Badge variant={variants[status] || 'secondary'} className="capitalize">
          {getStatusText(status)}
        </Badge>
        {metadata?.quality_score && (
          <Badge variant="outline" className="text-xs">
            Quality: {metadata.quality_score}/100
          </Badge>
        )}
      </div>
    );
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = !searchTerm || 
      report.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.examination_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.key_findings.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleCopyReport = async () => {
    if (!selectedReport) return;

    // Copy ONLY the AI-generated structured report (not key findings, not metadata)
    const reportText = selectedReport.detailed_report || 'Report not yet generated';

    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(reportText);
      
      toast({
        title: "AI Report Copied!",
        description: "Structured report copied to clipboard. Paste into Word or any editor.",
      });
      
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      
      // Fallback: Create a textarea and select the text
      const textArea = document.createElement('textarea');
      textArea.value = reportText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        toast({
          title: "AI Report Copied!",
          description: "Structured report copied. Paste into Word or any editor.",
        });
      } catch (fallbackError) {
        document.body.removeChild(textArea);
        toast({
          title: "Copy Failed",
          description: "Unable to copy. Please select and copy the report text manually.",
          variant: "destructive",
        });
      }
    }
  };

  const handlePrintReport = () => {
    if (selectedReport && selectedReport.detailed_report) {
      const printWindow = window.open('', '_blank');
      
      // Format the detailed report with proper HTML bold markers
      let formattedReport = selectedReport.detailed_report
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // **bold** → <strong>
        .replace(/\n/g, '<br>')  // newlines → <br>
        .replace(/(TECHNIQUE:|CLINICAL INFORMATION:|FINDINGS:|IMPRESSION:|RECOMMENDATIONS:)/g, '<br><strong style="font-size:12pt;text-transform:uppercase">$1</strong>')
        .replace(/(•)/g, '&nbsp;&nbsp;•');  // indent bullets
      
      // Build patient info section
      let patientHtml = '';
      const hasPatientInfo = selectedReport.patient_name || selectedReport.patient_age || selectedReport.referred_by;
      if (hasPatientInfo) {
        patientHtml = '<div class="patient-info">';
        if (selectedReport.patient_name) {
          let nameInfo = `<strong>Patient:</strong> ${selectedReport.patient_name}`;
          if (selectedReport.patient_age) nameInfo += ` / ${selectedReport.patient_age}`;
          if (selectedReport.patient_sex) nameInfo += ` / ${selectedReport.patient_sex}`;
          patientHtml += `<p>${nameInfo}</p>`;
        }
        if (selectedReport.referred_by) {
          patientHtml += `<p><strong>Ref. By:</strong> ${selectedReport.referred_by}</p>`;
        }
        patientHtml += `<p><strong>Date:</strong> ${formatDate(selectedReport.created_at)}</p>`;
        patientHtml += '</div>';
      }
      
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${selectedReport.examination_type.replace('-', ' ').toUpperCase()} Report</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; margin: 0.75in; line-height: 1.5; font-size: 11pt; color: #000; }
            .patient-info { padding: 10px 0; border-bottom: 1px solid #ccc; margin-bottom: 15px; }
            .patient-info p { margin: 3px 0; }
            .report-content { font-size: 11pt; line-height: 1.6; }
            .footer { margin-top: 40px; text-align: center; font-size: 9pt; color: #888; border-top: 1px solid #ccc; padding-top: 10px; }
            @media print { 
              body { margin: 0.5in; } 
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${patientHtml}
          <div class="report-content">${formattedReport}</div>
          <div class="footer">
            <p>Generated by ADAS-E - Advanced Diagnostic Assistant System</p>
          </div>
        </body>
        </html>
      `;
      
      // Use Blob URL instead of document.write() (safer, avoids XSS execution context)
      const blob = new Blob([printContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      printWindow.location.href = blobUrl;
      printWindow.focus();

      // Auto-print after content loads
      setTimeout(() => {
        printWindow.print();
        // Release the blob URL after print starts
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      }, 1000);
    }
  };

  const handleExportReport = async (reportId, format) => {
    try {
      setLoading(true);
      
      toast({
        title: "Export Started",
        description: `Generating ${format.toUpperCase()} export...`,
      });
      
      const response = await axios.get(`${API}/reports/${reportId}/export/${format}`, {
        responseType: 'blob'
      });
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `report_${reportId.substring(0, 8)}.${format}`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="?([^"]+)"?/);
        if (matches) {
          filename = matches[1];
        }
      }
      
      // Check if the browser supports the File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          // Use File System Access API to let user choose location
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: `${format.toUpperCase()} files`,
              accept: {
                [format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']: [`.${format}`]
              }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(response.data);
          await writable.close();
          
          toast({
            title: "Export Complete",
            description: `${format.toUpperCase()} file saved to your chosen location`,
          });
          
        } catch (err) {
          if (err.name !== 'AbortError') {
            // Fallback to regular download if save picker fails
            fallbackDownload(response.data, filename, format);
          }
        }
      } else {
        // Fallback for browsers that don't support File System Access API
        fallbackDownload(response.data, filename, format);
      }
      
    } catch (error) {
      console.error('Error exporting report:', error);
      toast({
        title: "Export Failed",
        description: error.response?.data?.detail || `Failed to export ${format.toUpperCase()}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fallbackDownload = (data, filename, format) => {
    // Create download link for browsers without File System Access API
    const blob = new Blob([data], { 
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    
    // Make the download more prominent
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Download Ready",
      description: `${format.toUpperCase()} file ready for download. Check your browser's download bar or Downloads folder.`,
    });
  };

  const handleEnhanceReport = async (reportId) => {
    try {
      setLoading(true);
      
      toast({
        title: "Enhancement Started",
        description: "Enhancing report with advanced AI models...",
      });
      
      const response = await axios.post(`${API}/reports/${reportId}/enhance`);
      
      toast({
        title: "Success",
        description: "Report enhanced successfully with multi-model AI",
      });
      
      // Refresh reports and update selected report
      await fetchReports();
      
      // Update selected report if it's the one being enhanced
      if (selectedReport && selectedReport.id === reportId) {
        const updatedReports = await axios.get(`${API}/reports`);
        const updatedReport = updatedReports.data.find(r => r.id === reportId);
        setSelectedReport(updatedReport);
      }
      
    } catch (error) {
      console.error('Error enhancing report:', error);
      toast({
        title: "Enhancement Failed",
        description: error.response?.data?.detail || "Failed to enhance report",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth callback
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback onAuthSuccess={(userData) => {
      setUser(userData);
      setAuthLoading(false);
    }} />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <Clock className="h-12 w-12 animate-spin text-[#0f7178] mx-auto mb-4" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen app-shell">
      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricing} 
        onClose={() => setShowPricing(false)} 
        user={user}
        onPaymentSuccess={(updatedUser) => {
          setUser(updatedUser);
          // Refresh stats
          axios.get(`${API}/auth/stats`)
            .then(res => setUserStats(res.data.stats))
            .catch(() => {});
        }}
      />

      {/* User Stats Modal */}
      {showStats && (
        <UserStatsDashboard 
          user={user} 
          stats={userStats} 
          onClose={() => setShowStats(false)} 
        />
      )}

      {/* Admin Panel */}
      {showAdmin && (
        <AdminPanel onClose={() => setShowAdmin(false)} />
      )}
      
      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-6xl">
        {/* Header with User Info */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="GB ADAS" className="h-14 w-14 object-contain drop-shadow-lg" />
              <div>
                <h1 className="hero-title text-3xl sm:text-4xl" data-testid="app-title">ADAS-E</h1>
                <p className="font-mono-clin text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase mt-1 text-slate-500">
                  Here to Assist — Not to Replace
                </p>
              </div>
            </div>
            
            {/* User Menu */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Admin Button */}
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAdmin(true)}
                  className="h-10 rounded-xl font-bold bg-white border-2 border-slate-200 text-slate-800 hover:border-violet-500 hover:text-violet-700"
                  data-testid="admin-panel-btn"
                >
                  <Shield className="h-4 w-4 mr-1" /> Admin
                </Button>
              )}

              {/* Scan Credits Badge */}
              <div 
                className="flex items-center gap-2 bg-white h-10 px-3 sm:px-4 rounded-xl border-2 border-slate-200 cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => setShowStats(true)}
                title="Click to view account details"
                data-testid="scans-credit-badge"
              >
                <Zap className="h-4 w-4 text-blue-600" />
                <span className="font-display font-extrabold text-slate-900 text-sm sm:text-base">
                  {user.total_scans_available === -1 
                    ? '∞ Unlimited' 
                    : `${user.total_scans_available} Scans`}
                </span>
                {user.free_scans_remaining > 0 && (
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold hidden sm:inline-flex">
                    {user.free_scans_remaining} Free
                  </Badge>
                )}
              </div>

              {/* Buy More Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPricing(true)}
                className="h-10 rounded-xl font-bold text-white border-0 bg-slate-900 hover:bg-blue-700 hover:text-white transition-colors"
                data-testid="buy-scans-btn"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Buy</span>
              </Button>
              
              {/* User Profile */}
              <div className="flex items-center gap-2 sm:gap-3 bg-white h-10 px-2 sm:px-3 rounded-xl border-2 border-slate-200">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full" />
                ) : (
                  <User className="h-8 w-8 text-gray-400" />
                )}
                <span className="font-bold text-slate-800 text-sm hidden md:inline">{user.name}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="text-slate-500 hover:text-rose-600 p-1 h-7"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-[460px] h-14 p-1.5 rounded-2xl bg-slate-200/70">
            <TabsTrigger value="create" className="flex items-center gap-2 h-full rounded-xl font-display text-base font-extrabold data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-lg" data-testid="create-report-tab">
              <Plus className="h-4 w-4" />
              Create Report
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2 h-full rounded-xl font-display text-base font-extrabold data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-lg" data-testid="view-reports-tab">
              <FileText className="h-4 w-4" />
              View Reports ({reports.length})
            </TabsTrigger>
          </TabsList>

          {/* Create Report Tab */}
          <TabsContent value="create">
            <form onSubmit={handleSubmit} id="report-form" className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_50px_-30px_rgba(30,58,138,0.35)] p-5 sm:p-8" data-testid="create-report-card">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
              <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">New Report</h2>
              <span className="font-mono-clin text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Fill · Attach · Generate</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
              <div className="space-y-5">
                <div>
                  <Label className="form-label-bold">Investigation <span className="text-rose-600">*</span></Label>
                  <Select value={formData.examination_type} onValueChange={(v) => handleInputChange('examination_type', v)}>
                    <SelectTrigger data-testid="examination-type-select" className="input-bold">
                      <SelectValue placeholder="MRI / CT / USG / X-Ray..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EXAMINATION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label-bold">Body Part</Label>
                  <div className="flex gap-2">
                    <Input
                      id="body_part_region"
                      data-testid="body-part-region-input"
                      placeholder="Brain, Knee, Spine..."
                      value={formData.body_part_region}
                      onChange={(e) => handleInputChange('body_part_region', e.target.value)}
                      className="input-bold flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const current = formData.body_part_region || '';
                        const hasRT = current.includes('RT') || current.includes('Right');
                        const hasLT = current.includes('LT') || current.includes('Left');
                        if (!hasRT && !hasLT) {
                          handleInputChange('body_part_region', 'RT ' + current);
                        } else if (hasRT) {
                          handleInputChange('body_part_region', current.replace(/\b(RT|Right)\b\s*/i, 'LT '));
                        } else {
                          handleInputChange('body_part_region', current.replace(/\b(LT|Left)\b\s*/i, 'RT '));
                        }
                      }}
                      className="h-12 w-16 shrink-0 rounded-xl font-display text-base font-black tracking-wider text-white bg-slate-900 hover:bg-blue-700 shadow-md transition-colors active:scale-95"
                      title={`Click to toggle: ${(formData.body_part_region || '').match(/\b(LT|Left)\b/i) ? 'Currently LEFT → Click for RIGHT' : 'Currently RIGHT → Click for LEFT'}`}
                      data-testid="side-toggle-btn"
                    >
                      {(formData.body_part_region || '').match(/\b(LT|Left)\b/i) ? 'LT' : 'RT'}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="form-label-bold">Clinical History</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="clinical_history"
                      data-testid="clinical-history-textarea"
                      placeholder="Headache, H/O trauma, Known diabetic..."
                      value={formData.clinical_history}
                      onChange={(e) => handleInputChange('clinical_history', e.target.value)}
                      className="input-bold flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => startVoiceForField('clinical_history')}
                      disabled={loading || (isTranscribing && activeVoiceField === 'clinical_history')}
                      className={`mic-bold !h-12 !w-12 shrink-0 ${isRecording && activeVoiceField === 'clinical_history' ? 'recording' : ''}`}
                      title="Dictate clinical history"
                      data-testid="clinical-history-voice-btn"
                    >
                      {isRecording && activeVoiceField === 'clinical_history' ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="form-label-bold">Diagnostic Clue <span className="normal-case tracking-normal font-semibold text-slate-400">(optional)</span></Label>
                  <Input
                    id="diagnostic_clues"
                    data-testid="diagnostic-clues-input"
                    placeholder="? ILD"
                    value={formData.diagnostic_clues}
                    onChange={(e) => handleInputChange('diagnostic_clues', e.target.value)}
                    className="input-bold"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="form-label-bold">Scan Images</Label>
                  <input
                    type="file"
                    id="image-upload"
                    accept=".jpg,.jpeg,.png,.bmp,.gif,.tiff,.tif,.webp,.dcm,.dicom,.zip,.mp4,.avi,.mov,.wmv,.flv,.mkv,.webm,.mpeg,.mpg"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload').click()}
                    className={`dropzone-bold ${uploadedImages.length > 0 ? 'has-files' : ''}`}
                    data-testid="choose-files-btn"
                  >
                    <span className="h-12 w-12 rounded-2xl bg-white shadow-md flex items-center justify-center">
                      {uploadedImages.length > 0 ? <CheckCircle className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
                    </span>
                    <span className="font-display text-lg font-extrabold">
                      {uploadedImages.length > 0 ? `${uploadedImages.length} FILE${uploadedImages.length>1?'S':''} SELECTED` : 'Attach Scan Images'}
                    </span>
                    <span className="font-mono-clin text-[11px] font-bold uppercase tracking-wider text-slate-500">JPG · PNG · DICOM · ZIP · Video</span>
                  </button>
                </div>

                {imagePreviews.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto py-1">
                    {imagePreviews.slice(0, 4).map((preview, index) => (
                      <div key={preview.url || `${preview.name}-${index}`} className="relative shrink-0 w-16 h-16 rounded-xl border-2 border-slate-200 overflow-hidden group">
                        {preview.type.startsWith('image/') ? (
                          <img src={preview.url} alt={preview.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-slate-50">
                            <FileText className="h-5 w-5 text-slate-400" />
                          </div>
                        )}
                        <button type="button" onClick={() => handleRemoveImage(index)} className="absolute top-0 right-0 bg-rose-600 text-white rounded-bl-lg p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-btn-${index}`}>
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {imagePreviews.length > 4 && (
                      <div className="shrink-0 w-16 h-16 rounded-xl border-2 border-slate-200 flex items-center justify-center bg-slate-50 text-sm font-bold text-slate-500">
                        +{imagePreviews.length - 4}
                      </div>
                    )}
                  </div>
                )}

                <details className="group rounded-xl border-2 border-slate-100 px-4 py-3 open:bg-slate-50">
                  <summary className="cursor-pointer select-none text-sm font-bold text-slate-700 hover:text-blue-700 transition-colors" data-testid="pacs-url-toggle">PACS / Cloud URL</summary>
                  <Input
                    type="url"
                    placeholder="https://pacs.hospital.com/study.zip"
                    value={pacsUrl}
                    onChange={(e) => setPacsUrl(e.target.value)}
                    className="input-bold mt-3"
                    data-testid="pacs-url-input"
                  />
                </details>

                <details className="group rounded-xl border-2 border-slate-100 px-4 py-3 open:bg-slate-50">
                  <summary className="cursor-pointer select-none text-sm font-bold text-slate-700 hover:text-blue-700 transition-colors" data-testid="patient-details-toggle">Patient Details</summary>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Input data-testid="patient-name-input" placeholder="Name" value={formData.patient_name} onChange={(e) => handleInputChange('patient_name', e.target.value)} className="h-10 text-sm bg-white border-slate-200 rounded-lg" />
                    <div className="flex gap-1">
                      <Input data-testid="patient-age-input" placeholder="Age" value={formData.patient_age} onChange={(e) => handleInputChange('patient_age', e.target.value)} className="h-10 text-sm bg-white border-slate-200 rounded-lg" />
                      <Select value={formData.patient_sex} onValueChange={(v) => handleInputChange('patient_sex', v)}>
                        <SelectTrigger data-testid="patient-sex-select" className="h-10 text-sm bg-white border-slate-200 w-20 rounded-lg"><SelectValue placeholder="Sex" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">M</SelectItem>
                          <SelectItem value="Female">F</SelectItem>
                          <SelectItem value="Other">O</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input data-testid="referred-by-input" placeholder="Referred By" value={formData.referred_by} onChange={(e) => handleInputChange('referred_by', e.target.value)} className="h-10 text-sm bg-white border-slate-200 rounded-lg" />
                    <Input type="date" data-testid="report-date-input" defaultValue={new Date().toISOString().split('T')[0]} className="h-10 text-sm bg-white border-slate-200 rounded-lg" />
                  </div>
                </details>
              </div>
            </div>

            {isAnalyzingImages && (
              <div className="mt-6 text-center py-3 rounded-xl bg-blue-50 text-blue-700 font-semibold text-sm animate-pulse" data-testid="analyzing-status">
                Analyzing images... ~60-90s
              </div>
            )}

            <div className="mt-6">
              <Button 
                type="button"
                onClick={() => {
                  if (uploadedImages.length > 0 && formData.examination_type && !formData.key_findings) {
                    handleAnalyzeImages();
                  } else {
                    document.getElementById('report-form').requestSubmit();
                  }
                }}
                className="btn-primary-bold"
                disabled={loading || isAnalyzingImages || (!formData.examination_type)}
                data-testid="analyse-report-btn"
              >
                {loading ? (
                  <><Clock className="mr-2 h-5 w-5 animate-spin" /> Generating Report...</>
                ) : isAnalyzingImages ? (
                  <><Clock className="mr-2 h-5 w-5 animate-spin" /> Analyzing Images...</>
                ) : uploadedImages.length > 0 && !formData.key_findings ? (
                  <><Zap className="mr-2 h-5 w-5" /> Analyse Images</>
                ) : (
                  <><FileText className="mr-2 h-5 w-5" /> Generate Report</>
                )}
              </Button>
            </div>
            </div>

            {(formData.key_findings || (!uploadedImages.length && formData.examination_type)) && (
              <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_50px_-30px_rgba(30,58,138,0.35)] p-5 sm:p-8 space-y-3 fade-up" data-testid="key-findings-card">
                <div className="flex items-center justify-between">
                  <Label className="form-label-bold !mb-0">Key Findings <span className="text-rose-600">*</span></Label>
                  <button
                    type="button"
                    onClick={() => isRecording ? stopVoiceRecognition() : startVoiceRecognition()}
                    disabled={loading || isTranscribing}
                    className={`mic-bold !h-11 !w-11 ${isRecording ? 'recording' : isTranscribing ? 'processing animate-pulse' : ''}`}
                    title="Dictate findings"
                    data-testid="key-findings-voice-btn"
                  >
                    {isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs font-semibold text-teal-700" data-testid="whisper-helper-text">Words auto-corrected by AI after entering</p>
                {(isRecording || isTranscribing) && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${isTranscribing ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700 animate-pulse'}`} data-testid="recording-status">
                    <div className={`w-2.5 h-2.5 rounded-full ${isTranscribing ? 'bg-amber-500' : 'bg-rose-500'} animate-pulse`}></div>
                    {isTranscribing ? 'Processing...' : 'Recording — tap mic to stop'}
                  </div>
                )}
                <Textarea
                  id="key_findings"
                  data-testid="key-findings-textarea"
                  placeholder="Type findings or use voice... EDH RT FTP 17mm, b/l effusion..."
                  value={formData.key_findings}
                  onChange={(e) => handleInputChange('key_findings', e.target.value)}
                  rows={4}
                  required
                  form="report-form"
                  className="bg-white border-2 border-slate-200 focus-visible:ring-0 focus-visible:border-blue-600 rounded-xl text-base font-medium resize-y"
                />
                {formData.key_findings && (
                  <Button 
                    type="submit" 
                    form="report-form"
                    className="btn-primary-bold"
                    disabled={loading}
                    data-testid="generate-report-btn"
                  >
                    {loading ? <><Clock className="mr-2 h-5 w-5 animate-spin" /> Generating...</> : <><FileText className="mr-2 h-5 w-5" /> Generate Report</>}
                  </Button>
                )}
              </div>
            )}

            {reports.length > 0 && (
              <button
                type="button"
                onClick={() => setActiveTab('reports')}
                className="w-full text-center text-sm font-bold text-slate-500 hover:text-blue-700 py-2 transition-colors"
                data-testid="view-reports-btn"
              >
                View Reports ({reports.length}) →
              </button>
            )}

            </form>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            {/* Storage Warning */}
            {reports.length > 50 && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-orange-800">High Number of Reports ({reports.length})</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        You have {reports.length} reports stored. Consider deleting old reports you no longer need to keep your system organized.
                        Use the checkboxes to select multiple reports for bulk deletion.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Search and Filter */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search reports by patient name, examination type, or findings..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-reports-input"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <Filter className="mr-2 h-4 w-4" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="generated">Generated</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Reports List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Reports List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Reports List</h3>
                  {filteredReports.length > 0 && (
                    <div className="flex items-center gap-2">
                      {selectedReportsForDelete.length > 0 && (
                        <>
                          <span className="text-sm text-gray-600">
                            {selectedReportsForDelete.length} selected
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleBulkDelete}
                            disabled={loading}
                            className="bg-red-500 text-white hover:bg-red-600"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete Selected
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={clearSelection}
                          >
                            Clear
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectedReportsForDelete.length === filteredReports.length ? clearSelection : selectAllReports}
                      >
                        {selectedReportsForDelete.length === filteredReports.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                  )}
                </div>
                {loading ? (
                  <div className="text-center py-8">
                    <Clock className="mx-auto h-8 w-8 animate-spin text-[#0d5c63]" />
                    <p className="mt-2 text-gray-600">Loading reports...</p>
                  </div>
                ) : filteredReports.length === 0 ? (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="text-center py-8">
                      <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">No reports found</p>
                    </CardContent>
                  </Card>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-3">
                      {filteredReports.map((report) => (
                        <Card 
                          key={report.id} 
                          className={`border-0 shadow-md cursor-pointer transition-all hover:shadow-lg ${
                            selectedReport?.id === report.id ? 'ring-2 ring-[#0d5c63] shadow-lg' : ''
                          } ${
                            selectedReportsForDelete.includes(report.id) ? 'ring-2 ring-red-500 bg-red-50' : ''
                          }`}
                          onClick={() => handleSelectReport(report)}
                          data-testid={`report-card-${report.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={selectedReportsForDelete.includes(report.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleReportSelection(report.id);
                                  }}
                                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                                {getStatusIcon(report.status)}
                                <h4 className="font-semibold text-gray-900 capitalize">
                                  {report.examination_type.replace('-', ' ')}
                                </h4>
                              </div>
                              <div className="flex items-center gap-1">
                                {getStatusBadge(report.status, report.metadata)}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReport(report.id, report.patient_name);
                                  }}
                                  disabled={loading}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                  title="Delete Report"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                            
                            {(report.patient_name || report.patient_id) && (
                              <p className="text-sm text-gray-600 mb-1">
                                <strong>Patient:</strong> {report.patient_name || 'N/A'}
                                {report.patient_id && ` (${report.patient_id})`}
                              </p>
                            )}
                            
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                              <strong>Key Findings:</strong> {report.key_findings}
                            </p>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Calendar className="h-3 w-3" />
                                {formatDate(report.created_at)}
                              </div>
                              {(report.status === 'generated' || report.status === 'enhanced') && (
                                <div className="flex gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExportReport(report.id, 'pdf');
                                    }}
                                    disabled={loading}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Export PDF - Choose Save Location"
                                  >
                                    <FileDown className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExportReport(report.id, 'docx');
                                    }}
                                    disabled={loading}
                                    className="p-1 text-[#0d5c63] hover:bg-[#f0f9f9] rounded transition-colors"
                                    title="Export Word - Choose Save Location"
                                  >
                                    <Download className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handleSelectReport(report);
                                      setTimeout(() => handleCopyReport(), 200);
                                    }}
                                    disabled={loading}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                    title="Copy Report Text"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteReport(report.id, report.patient_name);
                                    }}
                                    disabled={loading}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Delete Report"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>

              {/* Report Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Report Details</h3>
                {selectedReport ? (
                  <Card className="border-0 shadow-lg">
                    <CardHeader style={{ background: 'linear-gradient(135deg, #f0f9f9 0%, #e8f4f4 100%)' }}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="capitalize flex items-center gap-2">
                          {getStatusIcon(selectedReport.status)}
                          {selectedReport.examination_type.replace('-', ' ')} Report
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(selectedReport.status, selectedReport.metadata)}
                          <div className="flex gap-1">
                            {selectedReport.status !== 'enhanced' && selectedReport.status !== 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEnhanceReport(selectedReport.id)}
                                disabled={loading}
                                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white hover:from-amber-600 hover:to-yellow-700"
                              >
                                <Zap className="h-3 w-3 mr-1" />
                                Enhance
                              </Button>
                            )}
                            {(selectedReport.status === 'generated' || selectedReport.status === 'enhanced') && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExportReport(selectedReport.id, 'pdf')}
                                  disabled={loading}
                                  className="bg-red-500 text-white hover:bg-red-600"
                                  title="Export PDF - Choose where to save"
                                >
                                  <FileDown className="h-3 w-3 mr-1" />
                                  Save PDF
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExportReport(selectedReport.id, 'docx')}
                                  disabled={loading}
                                  className="bg-[#f0f9f9]0 text-white hover:bg-[#0d5c63]"
                                  title="Export Word - Choose where to save"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  Save Word
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCopyReport}
                                  disabled={loading}
                                  className="bg-green-600 text-white hover:bg-green-700"
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy Text
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handlePrintReport}
                                  disabled={loading}
                                  className="bg-gray-600 text-white hover:bg-gray-700"
                                >
                                  <Printer className="h-3 w-3 mr-1" />
                                  Print
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteReport(selectedReport.id, selectedReport.patient_name)}
                                  disabled={loading}
                                  className="bg-red-500 text-white hover:bg-red-600"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {(selectedReport.patient_name || selectedReport.patient_id) && (
                        <CardDescription className="text-teal-100 patient-info-text">
                          Patient: {selectedReport.patient_name || 'N/A'}
                          {selectedReport.patient_id && ` (ID: ${selectedReport.patient_id})`}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="p-6">
                      <ScrollArea className="h-[500px]">
                        <div className="space-y-4">
                          {/* Key Findings */}
                          <div>
                            <h4 className="report-section-header">Key Findings</h4>
                            <div className="key-findings report-content">
                              {selectedReport.key_findings}
                            </div>
                          </div>

                          {selectedReport.clinical_history && (
                            <div>
                              <h4 className="report-section-header">Clinical History</h4>
                              <div className="clinical-history report-content">
                                {selectedReport.clinical_history}
                              </div>
                            </div>
                          )}

                          <Separator />

                          {/* Enhanced AI Metadata */}
                          {selectedReport.metadata && selectedReport.metadata.enhancement_applied && (
                            <div>
                              <h4 className="report-section-header">Enhanced AI Processing</h4>
                              <div className="bg-[#f0f9f9] p-3 rounded-lg border border-[#0d5c63]/20">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="font-medium text-[#094147]">Models Used:</span>
                                    <div className="text-[#094147] text-xs mt-1">
                                      {selectedReport.metadata.models_used?.join(', ') || 'Multiple AI models'}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-[#094147]">Quality Score:</span>
                                    <div className="text-[#094147] text-xs mt-1">
                                      {selectedReport.metadata.quality_score || 'N/A'}/100
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-[#094147]">Generation Time:</span>
                                    <div className="text-[#094147] text-xs mt-1">
                                      {selectedReport.metadata.generation_time_seconds 
                                        ? `${selectedReport.metadata.generation_time_seconds.toFixed(1)}s` 
                                        : 'N/A'}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-[#094147]">Enhancement:</span>
                                    <div className="text-[#094147] text-xs mt-1">
                                      Multi-model + Refinement
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          <Separator />

                          {/* Export Information */}
                          {(selectedReport.status === 'generated' || selectedReport.status === 'enhanced') && (
                            <div>
                              <h4 className="report-section-header">Export Options</h4>
                              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                <div className="text-sm text-green-800 space-y-2">
                                  <p>
                                    <strong>📋 Copy Text (Easiest):</strong> Click "Copy Text" to copy the entire report. 
                                    Then paste into Word, Notepad, or any text editor and save wherever you want!
                                  </p>
                                  <p>
                                    <strong>💾 Direct Save:</strong> Use "Save PDF" or "Save Word" buttons. 
                                    Modern browsers will let you choose the save location, others save to Downloads folder.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          <Separator />

                          {/* Detailed Report */}
                          <div>
                            <h4 className="report-section-header">AI-Generated Detailed Report</h4>
                            {isLoadingReportDetails ? (
                              <div className="text-center py-8">
                                <Clock className="mx-auto h-8 w-8 animate-spin text-[#0d5c63]" />
                                <p className="mt-2 text-gray-600">Loading report details...</p>
                              </div>
                            ) : (selectedReport.status === 'generated' || selectedReport.status === 'enhanced') && selectedReport.detailed_report ? (
                              <div className="report-content max-w-none bg-white p-4 rounded-lg border">
                                <pre className="whitespace-pre-wrap">
                                  {selectedReport.detailed_report}
                                </pre>
                              </div>
                            ) : (selectedReport.status === 'pending' || selectedReport.status === 'generating') ? (
                              <div className="text-center py-8">
                                <Clock className="mx-auto h-8 w-8 animate-spin text-[#0d5c63]" />
                                <p className="mt-2 text-gray-600">Generating detailed report...</p>
                              </div>
                            ) : selectedReport.status === 'error' ? (
                              <div className="text-center py-8">
                                <XCircle className="mx-auto h-8 w-8 text-red-500" />
                                <p className="mt-2 text-red-600">Failed to generate report</p>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="mt-2"
                                  onClick={fetchReports}
                                >
                                  Retry
                                </Button>
                              </div>
                            ) : (
                              <p className="text-gray-600 italic">No detailed report available</p>
                            )}
                          </div>

                          <Separator />

                          {/* AI Disclaimer */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-semibold text-amber-800 text-sm">AI-Generated Content Disclaimer</h4>
                                <p className="text-amber-700 text-xs mt-1">
                                  This report was generated using artificial intelligence and is intended for reference purposes only. 
                                  All findings and recommendations must be reviewed and verified by a qualified radiologist before 
                                  clinical use. AI-generated content may contain errors and should not be used as the sole basis 
                                  for medical decisions.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Report Info */}
                          <div className="text-sm text-gray-500">
                            <p><strong>Report ID:</strong> {selectedReport.id}</p>
                            <p><strong>Created:</strong> {formatDate(selectedReport.created_at)}</p>
                          </div>
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-0 shadow-lg">
                    <CardContent className="text-center py-12">
                      <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600">Select a report to view details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Toaster />
    </div>
  );
}

export default App;
